import { useMutation, useQuery, useQueryClient } from "react-query";
import { shopHttp } from "./shop-http";
import { CreateProviderPaymentPoints } from "../models/CreateProviderPaymentPoints";
import { GetProviderPaymentPointsData } from "../models/GetProviderPaymentPointsSuccessResponse";
import { OrganizationKeyName } from "../../../model/Organization";
import { LinkProductToShopType } from "../../../service/api-response/LinkProductToShop";
import { UnlinkProductToShopType } from "../../../service/api-response/UnlinkProductFromShop";
import {
  CreateSalesChannel,
  UpdateSalesChannel,
} from "../models/sales-channel";
import { ShopListItem } from "../model/ShopListSuccessResponse";
import { CreateShop, UpdateShop } from "../model/ShopSuccessResponse";

const cacheKeys = {
  providerPaymentPoints: "providerPaymentPoints",
  products: "products",
  product: "product",
  shop: "shop",
  salesChannels: "salesChannels",
  salesChannelEgressConnections: "salesChannelEgressConnections",
  salesChannel: "salesChannel",
  shops: "shops",
  productCategories: "productCategories",
  timeslots: "timeslots",
  adyenStores: "adyenStores",
  paymentProviders: "paymentProviders",
  paymentProvider: "paymentProvider",
};

export const useGetProviderPaymentPoints = (
  organizationKeyName: string,
  shopId?: string
) => {
  return useQuery<GetProviderPaymentPointsData[], any>(
    [cacheKeys.providerPaymentPoints, shopId],
    () => shopHttp.getProviderPaymentPoints(organizationKeyName, shopId),
    { refetchOnWindowFocus: false, enabled: !!shopId }
  );
};

export const useGetSalesChannelEgressConnections = (
  organizationKeyName: string,
  salesChannelId?: string
) => {
  return useQuery(
    [cacheKeys.salesChannelEgressConnections, salesChannelId],
    () =>
      shopHttp.getSalesChannelEgressConnections(
        organizationKeyName,
        salesChannelId
      ),
    { refetchOnWindowFocus: false, enabled: !!salesChannelId }
  );
};

export const useCreateSalesChannelEgressConnection = (
  salesChannelId?: string
) => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.createSalesChannelEgressConnection, {
    onSuccess: async () => {
      if (salesChannelId) {
        await queryClient.refetchQueries([
          cacheKeys.salesChannelEgressConnections,
          salesChannelId,
        ]);
      }
    },
  });
};

export const useUpdateSalesChannelEgressConnection = (
  salesChannelId?: string
) => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.updateSalesChannelEgressConnection, {
    onSuccess: async () => {
      if (salesChannelId) {
        await queryClient.refetchQueries([
          cacheKeys.salesChannelEgressConnections,
          salesChannelId,
        ]);
      }
    },
  });
};

export const useDeleteSalesChannelEgressConnection = (
  salesChannelId?: string
) => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.deleteSalesChannelEgressConnection, {
    onSuccess: async () => {
      if (salesChannelId) {
        await queryClient.refetchQueries([
          cacheKeys.salesChannelEgressConnections,
          salesChannelId,
        ]);
      }
    },
  });
};

export const useInsertProviderPaymentPoints = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, CreateProviderPaymentPoints>(
    shopHttp.insertProviderPaymentPoints,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(cacheKeys.providerPaymentPoints);
      },
    }
  );
};

export const useGetSalesChannels = (
  organizationKeyName: OrganizationKeyName,
  shopId?: string
) => {
  return useQuery(
    [cacheKeys.salesChannels, shopId],
    () => shopHttp.getSalesChannels(organizationKeyName, shopId),
    { refetchOnWindowFocus: false, enabled: !!shopId }
  );
};

export const useGetSalesChannel = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery(
    [cacheKeys.salesChannel, id],
    () => shopHttp.getSalesChannel(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useUpdateSalesChannel = (id?: string, shopId?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateSalesChannel>(
    shopHttp.updateSalesChannel,
    {
      onSuccess: async () => {
        if (shopId) {
          queryClient.refetchQueries([cacheKeys.salesChannels, shopId], {
            exact: true,
          });
        }
        if (id) {
          queryClient.refetchQueries([cacheKeys.salesChannel, id]);
        }
      },
    }
  );
};

export const useCreateSalesChannel = () => {
  const queryClient = useQueryClient();
  return useMutation<string, any, CreateSalesChannel>(
    shopHttp.createSalesChannel,
    {
      onSuccess: async () => {
        queryClient.refetchQueries([cacheKeys.salesChannels]);
      },
    }
  );
};

export const useLinkProductToShop = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, LinkProductToShopType>(
    shopHttp.linkProductToShop,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(cacheKeys.products);
      },
    }
  );
};

export const useUnlinkProductToShop = () => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UnlinkProductToShopType>(
    shopHttp.unlinkProductToShop,
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(cacheKeys.products);
      },
    }
  );
};

export const useGetShop = (
  organizationKeyName: OrganizationKeyName,
  id?: string
) => {
  return useQuery(
    [cacheKeys.shop, id],
    () => shopHttp.getShop(organizationKeyName, id),
    { refetchOnWindowFocus: false, enabled: !!id }
  );
};

export const useGetShops = (
  organizationKeyName: OrganizationKeyName,
  enabled: boolean = true
) => {
  return useQuery<ShopListItem[], any>(
    [cacheKeys.shops],
    () => shopHttp.getShops(organizationKeyName),
    { refetchOnWindowFocus: false, enabled }
  );
};

export const useCreateShop = () => {
  const queryClient = useQueryClient();
  return useMutation<string, any, CreateShop>(shopHttp.createShop, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.shops]);
    },
  });
};

export const useUpdateShop = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<any, any, UpdateShop>(shopHttp.updateShop, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.shop, id]);
    },
  });
};

export const useListTimeslots = (
  organizationKeyName: OrganizationKeyName,
  shopId: string,
  enabled: boolean = true
) => {
  return useQuery(
    [cacheKeys.timeslots, shopId],
    () => shopHttp.listTimeslots(organizationKeyName, shopId),
    { refetchOnWindowFocus: false, enabled }
  );
};

export const useCopyAndSetTimeslotsFromShop = () => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.copyAndSetTimeslotsFromShop, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.timeslots]);
    },
  });
};

export const useCreateTimeslotTemplate = (shopId?: string) => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.createTimeslotTemplate, {
    onSuccess: async () => {
      // Seems like the creation is being handled async, so introducing a delay to increase the chance of the timeslots having been created before we refetch
      if (shopId) {
        setTimeout(() => {
          queryClient.refetchQueries([cacheKeys.timeslots, shopId]);
        }, 5000);
      }
    },
  });
};

export const useUpdateTimeslot = () => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.updateTimeslot, {
    onSuccess: async () => {
      queryClient.refetchQueries([cacheKeys.timeslots]);
    },
  });
};

export const useGetAdyenStoresForOrganization = (
  organizationKeyName: OrganizationKeyName
) => {
  return useQuery(
    [cacheKeys.adyenStores, organizationKeyName],
    () => shopHttp.getAdyenStoresForOrganization(organizationKeyName),
    { refetchOnWindowFocus: false }
  );
};

export const useGetPaymentProviders = ({
  organizationKeyName,
  shopId,
}: {
  organizationKeyName: OrganizationKeyName;
  shopId: string;
}) => {
  return useQuery(
    [cacheKeys.paymentProviders, shopId, organizationKeyName],
    () => shopHttp.getPaymentProviders({ organizationKeyName, shopId }),
    { refetchOnWindowFocus: false, enabled: !!shopId }
  );
};

export const useGetPaymentProvider = ({
  organizationKeyName,
  shopId,
  id,
}: {
  organizationKeyName: OrganizationKeyName;
  shopId: string;
  id: string;
}) => {
  return useQuery(
    [cacheKeys.paymentProvider, id],
    () => shopHttp.getPaymentProvider({ organizationKeyName, shopId, id }),
    { refetchOnWindowFocus: false }
  );
};

export const useUpdatePaymentProvider = ({
  shopId,
  organizationKeyName,
}: {
  shopId?: string;
  organizationKeyName?: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.updateShopPaymentProvider, {
    onSuccess: async (id) => {
      queryClient.refetchQueries([
        cacheKeys.paymentProviders,
        shopId,
        organizationKeyName,
      ]);
      queryClient.refetchQueries([cacheKeys.paymentProvider, id]);
    },
  });
};

export const useDeleteShopPaymentProvider = ({
  shopId,
  organizationKeyName,
}: {
  shopId?: string;
  organizationKeyName?: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.deleteShopPaymentProvider, {
    onSuccess: async () => {
      queryClient.refetchQueries([
        cacheKeys.paymentProviders,
        shopId,
        organizationKeyName,
      ]);
    },
  });
};

export const useAddPaymentProviderToShop = ({
  shopId,
  organizationKeyName,
}: {
  shopId?: string;
  organizationKeyName?: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation(shopHttp.addShopPaymentProvider, {
    onSuccess: async () => {
      queryClient.refetchQueries([
        cacheKeys.paymentProviders,
        shopId,
        organizationKeyName,
      ]);
    },
  });
};
